import { addMonths, format, parseISO, startOfMonth } from 'date-fns';

import { CashFlowConfig } from '@ndvr/portfolio-api';

import { projectedWithdrawalAmount } from 'views/portfolio/policy-withdrawals';
import { getNextCashFlowOccurrence } from 'views/portfolio/PortfolioPage/tabs/liquidity-planning/cash-flow-schedule';
import { getFactorOccurrences } from 'views/portfolio/PortfolioPage/utils/factor-date-frame-utils';

/** A run of whole months, starting at the first of `start`'s month. */
export interface ChartWindow {
    start: Date;
    months: number;
}

export interface ChartMonth {
    month: Date;
    /** Scheduled withdrawals falling in the month. */
    total: number;
}

export const MAX_WINDOW_MONTHS = 36;

export const windowEnd = ({ start, months }: ChartWindow) => addMonths(start, months);

export const monthIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth();

export const nextMonths = (today: Date, months: number): ChartWindow => ({ start: startOfMonth(today), months });

/**
 * The next 12 months, or, when nothing is scheduled that soon (a plan that starts at retirement, say), the 12 months
 * from the first upcoming withdrawal, so the chart opens on something instead of an empty period.
 */
export const defaultWindow = (configs: CashFlowConfig[], today: Date): ChartWindow => {
    const upcoming = nextMonths(today, 12);
    const first = configs
        .map((config) => getNextCashFlowOccurrence(config, today))
        .filter((date): date is string => date !== undefined)
        .sort()[0];
    if (!first || parseISO(first) < windowEnd(upcoming)) return upcoming;
    return { start: startOfMonth(parseISO(first)), months: 12 };
};

export const formatWindow = (window: ChartWindow) =>
    `${format(window.start, 'MMM yyyy')} – ${format(addMonths(window.start, window.months - 1), 'MMM yyyy')}`;

export const chartWindowPresets = (today: Date): { key: string; name: string; window: ChartWindow }[] => {
    const thisMonth = startOfMonth(today);
    return [
        { key: 'next12', name: 'Next 12 months', window: nextMonths(today, 12) },
        { key: 'last12', name: 'Last 12 months', window: { start: addMonths(thisMonth, -12), months: 12 } },
        {
            key: 'ytd',
            name: 'Year to date',
            window: { start: new Date(today.getFullYear(), 0, 1), months: thisMonth.getMonth() + 1 },
        },
        { key: 'around', name: 'Past 6 + next 6', window: { start: addMonths(thisMonth, -6), months: 12 } },
        { key: 'next24', name: 'Next 24 months', window: nextMonths(today, 24) },
        { key: 'next36', name: 'Next 36 months', window: nextMonths(today, 36) },
        {
            key: 'nextYear',
            name: `Calendar ${today.getFullYear() + 1}`,
            window: { start: new Date(today.getFullYear() + 1, 0, 1), months: 12 },
        },
    ];
};

/** Scheduled withdrawals per month of the window. */
export const monthlyWithdrawals = (configs: CashFlowConfig[], window: ChartWindow): ChartMonth[] => {
    const months: ChartMonth[] = Array.from({ length: window.months }, (_, index) => ({
        month: addMonths(window.start, index),
        total: 0,
    }));
    const first = monthIndex(window.start);
    for (const config of configs) {
        for (const date of getFactorOccurrences(config.factorDateFrame, window.start, windowEnd(window))) {
            const amount = projectedWithdrawalAmount(config, date);
            // Incomplete inflation settings make the amount unknown; leave the occurrence out rather than chart NaN.
            if (!Number.isFinite(amount)) continue;
            months[monthIndex(parseISO(date)) - first].total += amount;
        }
    }
    return months;
};

/** $56K / $950 / $2.5M, for labels over the bars. */
export const compactAmount = (value: number) => {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    return value >= 1000 ? `$${Math.round(value / 1000)}K` : `$${Math.round(value)}`;
};
